import { useCallback, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  CreateQuerySchema,
  QueryStatus,
  QueryType
} from "@/jenesys-types/data/communication";

import { useCompany } from "@/hooks/util/useCompany";
import {
  createQuery,
  deleteQuery,
  loadQueries,
  loadQueryDetails,
  postQueryComment,
  resolveQuery,
  selectCreateQueryError,
  selectCurrentQuery,
  selectDeleteQueryError,
  selectIsCreating,
  selectIsDeletingQuery,
  selectIsLoading,
  selectIsLoadingQueryDetails,
  selectIsPostingComment,
  selectIsResolvingQuery,
  selectPostCommentError,
  selectQueries,
  selectQueryDetailsError,
  selectResolveQueryError
} from "@/store/features/communicationsHub/communicationsHub";

export const useCommunicationQueries = (context?: "company" | "client") => {
  const [timeLastRefreshed, setTimeLastRefreshed] = useState<string>("");
  const { activeCompanyId, activeClientId } = useCompany();
  const dispatch = useDispatch();

  const [searchAndFilter, setSearchAndFilter] = useState<{
    search: string;
    queryType: QueryType;
    status: QueryStatus;
  }>({
    search: "",
    queryType: "All Types",
    status: "All Statuses"
  });

  const queries = useSelector(selectQueries);
  const isLoadingQueries = useSelector(selectIsLoading);
  const isCreating = useSelector(selectIsCreating);
  const createQueryError = useSelector(selectCreateQueryError);
  const isResolvingQuery = useSelector(selectIsResolvingQuery);
  const resolveQueryError = useSelector(selectResolveQueryError);
  const isDeletingQuery = useSelector(selectIsDeletingQuery);
  const deleteQueryError = useSelector(selectDeleteQueryError);

  const getFormattedTimestamp = useCallback(() => {
    const now = new Date();

    const time = now.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    });

    const date = now.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });

    return `${time}, ${date}`;
  }, []);

  const dispatchLoadQueries = useCallback(() => {
    const search =
      searchAndFilter.search && searchAndFilter.search !== "All Statuses"
        ? searchAndFilter.search
        : "";

    const queryType =
      searchAndFilter.queryType && searchAndFilter.queryType !== "All Types"
        ? searchAndFilter.queryType.toUpperCase()
        : "";

    dispatch(
      loadQueries(
        context === "company" ? activeCompanyId : activeClientId || "",
        search,
        queryType
      )
    );
    setTimeLastRefreshed(getFormattedTimestamp());
  }, [
    activeCompanyId,
    activeClientId,
    context,
    dispatch,
    getFormattedTimestamp,
    searchAndFilter
  ]);

  useEffect(() => {
    dispatchLoadQueries();
  }, [dispatchLoadQueries]);

  const createNewQuery = (
    queryData: CreateQuerySchema,
    onSuccess?: (newQueryId: string) => void
  ) => {
    dispatch(createQuery(queryData, onSuccess));
  };

  const resolveQueryAction = (queryId: string, onSuccess?: () => void) => {
    const companyId = context === "company" ? activeCompanyId : activeClientId;
    if (companyId) {
      dispatch(resolveQuery(companyId, queryId, onSuccess));
    }
  };

  const deleteQueryAction = (queryId: string, onSuccess?: () => void) => {
    const companyId = context === "company" ? activeCompanyId : activeClientId;
    if (companyId) {
      dispatch(deleteQuery(companyId, queryId, onSuccess));
    }
  };

  return {
    queries,
    isLoadingQueries,
    searchAndFilter,
    setSearchAndFilter,
    createNewQuery,
    isCreating,
    createQueryError,
    dispatchLoadQueries,
    timeLastRefreshed,
    resolveQueryAction,
    isResolvingQuery,
    resolveQueryError,
    deleteQueryAction,
    isDeletingQuery,
    deleteQueryError
  };
};

export const useQueryDetails = (queryId?: string, companyId?: string) => {
  const dispatch = useDispatch();
  const query = useSelector(selectCurrentQuery);
  const isLoading = useSelector(selectIsLoadingQueryDetails);
  const error = useSelector(selectQueryDetailsError);

  useEffect(() => {
    if (queryId && companyId) {
      dispatch(loadQueryDetails(companyId, queryId));
    }
  }, [dispatch, queryId, companyId]);

  return { query, isLoading, error };
};

export const usePostQueryComment = (queryId?: string, companyId?: string) => {
  const dispatch = useDispatch();
  const isPosting = useSelector(selectIsPostingComment);
  const error = useSelector(selectPostCommentError);

  const postComment = useCallback(
    (comment: string, onSuccess?: () => void) => {
      if (queryId && companyId && comment) {
        dispatch(postQueryComment(companyId, queryId, comment, onSuccess));
      }
    },
    [dispatch, queryId, companyId]
  );

  return { postComment, isPosting, error };
};
